import {
  Filter,
  GlProgram,
  GpuProgram,
  Matrix,
  Texture,
  UniformGroup,
  type FilterSystem,
  type RenderSurface,
  type Sprite,
  type Texture as TextureType,
} from 'pixi.js';

/**
 * A displacement filter for the sea, weighted by a mask: where the mask is
 * white the water shifts a few pixels to a drifting ripple pattern (two
 * layers of it, moving different ways, so it never reads as one sheet
 * sliding), with optional sun glints; where it's black nothing is drawn.
 * It goes on a copy of the map tiles drawn over the real ones, so the land
 * stays exactly as it was and the moving sea fades into the still one.
 *
 * Pixi's own DisplacementFilter, plus the mask and the second layer, in
 * one pass. The two sprites are only read for where they sit: `noise` is a
 * repeating ripple pattern, `mask` the sea mask placed over its stretch of
 * the painting. Both must be on the stage (they're never drawn).
 */
export class SeaRippleFilter extends Filter {
  private readonly noise: Sprite;
  private readonly mask: Sprite;

  constructor(noise: Sprite, mask: Sprite) {
    const uniforms = new UniformGroup({
      uNoiseMatrix: { value: new Matrix(), type: 'mat3x3<f32>' },
      uMaskMatrix: { value: new Matrix(), type: 'mat3x3<f32>' },
      uScale: { value: new Float32Array([0, 0]), type: 'vec2<f32>' },
      uDrift: { value: new Float32Array([0, 0, 0, 0]), type: 'vec4<f32>' },
      uGlint: { value: 0, type: 'f32' },
    });
    super({
      glProgram: GlProgram.from({ vertex: VERTEX, fragment: FRAGMENT, name: 'sea-ripple-filter' }),
      gpuProgram: GpuProgram.from({
        vertex: { source: WGSL, entryPoint: 'mainVertex' },
        fragment: { source: WGSL, entryPoint: 'mainFragment' },
      }),
      resources: {
        seaUniforms: uniforms,
        uNoiseTexture: noise.texture.source,
        uNoiseSampler: noise.texture.source.style,
        uMaskTexture: mask.texture.source,
        uMaskSampler: mask.texture.source.style,
      },
    });
    this.noise = noise;
    this.mask = mask;
    noise.renderable = false;
    mask.renderable = false;
  }

  /** How far the water shifts, in screen pixels. */
  set strength(px: number) {
    const u = this.resources.seaUniforms.uniforms;
    u.uScale[0] = px;
    u.uScale[1] = px;
  }

  /** The ripple layers' drift, in pattern repeats. */
  setDrift(ax: number, ay: number, bx: number, by: number): void {
    const d = this.resources.seaUniforms.uniforms.uDrift as Float32Array;
    d[0] = ax;
    d[1] = ay;
    d[2] = bx;
    d[3] = by;
  }

  set glint(v: number) {
    this.resources.seaUniforms.uniforms.uGlint = v;
  }

  override apply(
    filterManager: FilterSystem,
    input: TextureType,
    output: RenderSurface,
    clearMode: boolean,
  ): void {
    const u = this.resources.seaUniforms.uniforms;
    filterManager.calculateSpriteMatrix(u.uNoiseMatrix as Matrix, this.noise);
    filterManager.calculateSpriteMatrix(u.uMaskMatrix as Matrix, this.mask);
    filterManager.applyFilter(this, input, output, clearMode);
  }
}

/**
 * A repeating, smooth ripple pattern drawn in code (no download): red and
 * green carry two independent sums of waves whose frequencies are whole
 * numbers, so the pattern tiles without a seam.
 */
export function rippleTexture(size = 128): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(size, size);
  const waves = [
    [1, 2, 0.3, 0.9],
    [3, -1, 1.7, 0.6],
    [-2, 3, 4.1, 0.45],
    [4, 1, 2.6, 0.35],
    [1, -4, 5.3, 0.3],
  ] as const;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = (x / size) * Math.PI * 2;
      const v = (y / size) * Math.PI * 2;
      let r = 0;
      let g = 0;
      let total = 0;
      for (const [a, b, p, w] of waves) {
        r += w * Math.sin(a * u + b * v + p);
        g += w * Math.cos(b * u - a * v + p * 1.3);
        total += w;
      }
      const i = (y * size + x) * 4;
      img.data[i] = Math.round(127.5 + (127.5 * r) / total);
      img.data[i + 1] = Math.round(127.5 + (127.5 * g) / total);
      img.data[i + 2] = 128;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const texture = Texture.from(canvas);
  texture.source.style.addressMode = 'repeat';
  texture.source.style.update();
  return texture;
}

const VERTEX = /* glsl */ `in vec2 aPosition;
out vec2 vTextureCoord;
out vec2 vNoiseUv;
out vec2 vMaskUv;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;
uniform mat3 uNoiseMatrix;
uniform mat3 uMaskMatrix;

vec4 filterVertexPosition(void)
{
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
    return vec4(position, 0.0, 1.0);
}

vec2 filterTextureCoord(void)
{
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
}

void main(void)
{
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
    vNoiseUv = (uNoiseMatrix * vec3(vTextureCoord, 1.0)).xy;
    vMaskUv = (uMaskMatrix * vec3(vTextureCoord, 1.0)).xy;
}
`;

const FRAGMENT = /* glsl */ `in vec2 vTextureCoord;
in vec2 vNoiseUv;
in vec2 vMaskUv;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform sampler2D uNoiseTexture;
uniform sampler2D uMaskTexture;
uniform vec4 uInputClamp;
uniform highp vec4 uInputSize;
uniform vec2 uScale;
uniform vec4 uDrift;
uniform float uGlint;

void main()
{
    vec2 inside = step(vec2(0.0), vMaskUv) * step(vMaskUv, vec2(1.0));
    float m = texture(uMaskTexture, vMaskUv).r * inside.x * inside.y;
    vec2 a = texture(uNoiseTexture, vNoiseUv + uDrift.xy).xy - 0.5;
    vec2 b = texture(uNoiseTexture, vNoiseUv * 1.73 + uDrift.zw).yx - 0.5;
    vec2 n = a * 0.65 + b * 0.35;
    vec2 offset = uInputSize.zw * n * uScale * m;
    vec4 c = texture(uTexture, clamp(vTextureCoord + offset, uInputClamp.xy, uInputClamp.zw));
    // Glints: small specks where a finer copy of the pattern peaks.
    vec2 f = texture(uNoiseTexture, vNoiseUv * 4.3 + uDrift.yx * 2.1).xy - 0.5;
    float g = smoothstep(0.36, 0.47, f.x + f.y + n.x * 0.5) * m * uGlint;
    // Only the sea is drawn (faded in by the mask): the tiles underneath
    // show everywhere else, so this copy can be drawn at a lower resolution.
    finalColor = (c + vec4(g, g, g * 0.92, 0.0) * c.a) * m;
}
`;

const WGSL = /* wgsl */ `
struct GlobalFilterUniforms {
  uInputSize:vec4<f32>,
  uInputPixel:vec4<f32>,
  uInputClamp:vec4<f32>,
  uOutputFrame:vec4<f32>,
  uGlobalFrame:vec4<f32>,
  uOutputTexture:vec4<f32>,
};

struct SeaUniforms {
  uNoiseMatrix:mat3x3<f32>,
  uMaskMatrix:mat3x3<f32>,
  uScale:vec2<f32>,
  uDrift:vec4<f32>,
  uGlint:f32,
};

@group(0) @binding(0) var<uniform> gfu: GlobalFilterUniforms;
@group(0) @binding(1) var uTexture: texture_2d<f32>;
@group(0) @binding(2) var uSampler : sampler;

@group(1) @binding(0) var<uniform> seaUniforms : SeaUniforms;
@group(1) @binding(1) var uNoiseTexture: texture_2d<f32>;
@group(1) @binding(2) var uNoiseSampler : sampler;
@group(1) @binding(3) var uMaskTexture: texture_2d<f32>;
@group(1) @binding(4) var uMaskSampler : sampler;

struct VSOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) uv : vec2<f32>,
  @location(1) noiseUv : vec2<f32>,
  @location(2) maskUv : vec2<f32>,
};

fn filterVertexPosition(aPosition:vec2<f32>) -> vec4<f32>
{
  var position = aPosition * gfu.uOutputFrame.zw + gfu.uOutputFrame.xy;
  position.x = position.x * (2.0 / gfu.uOutputTexture.x) - 1.0;
  position.y = position.y * (2.0*gfu.uOutputTexture.z / gfu.uOutputTexture.y) - gfu.uOutputTexture.z;
  return vec4(position, 0.0, 1.0);
}

fn filterTextureCoord(aPosition:vec2<f32>) -> vec2<f32>
{
  return aPosition * (gfu.uOutputFrame.zw * gfu.uInputSize.zw);
}

@vertex
fn mainVertex(@location(0) aPosition : vec2<f32>) -> VSOutput {
  let uv = filterTextureCoord(aPosition);
  return VSOutput(
    filterVertexPosition(aPosition),
    uv,
    (seaUniforms.uNoiseMatrix * vec3(uv, 1.0)).xy,
    (seaUniforms.uMaskMatrix * vec3(uv, 1.0)).xy
  );
}

@fragment
fn mainFragment(
  @location(0) uv: vec2<f32>,
  @location(1) noiseUv: vec2<f32>,
  @location(2) maskUv: vec2<f32>,
) -> @location(0) vec4<f32> {
  let inside = step(vec2(0.0), maskUv) * step(maskUv, vec2(1.0));
  let m = textureSample(uMaskTexture, uMaskSampler, maskUv).r * inside.x * inside.y;
  let a = textureSample(uNoiseTexture, uNoiseSampler, noiseUv + seaUniforms.uDrift.xy).xy - 0.5;
  let b = textureSample(uNoiseTexture, uNoiseSampler, noiseUv * 1.73 + seaUniforms.uDrift.zw).yx - 0.5;
  let n = a * 0.65 + b * 0.35;
  let offset = gfu.uInputSize.zw * n * seaUniforms.uScale * m;
  let c = textureSample(uTexture, uSampler, clamp(uv + offset, gfu.uInputClamp.xy, gfu.uInputClamp.zw));
  let f = textureSample(uNoiseTexture, uNoiseSampler, noiseUv * 4.3 + seaUniforms.uDrift.yx * 2.1).xy - 0.5;
  let g = smoothstep(0.36, 0.47, f.x + f.y + n.x * 0.5) * m * seaUniforms.uGlint;
  return (c + vec4(g, g, g * 0.92, 0.0) * c.a) * m;
}
`;
